import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNotification } from "@/context/NotificationContext";
import { fetchTrades } from "@/api/fetchTrades/fetchTradesApi";
import { Trade } from "@/api/fetchTrades/types";
import { fetchBusinessUnits } from "@/api/fetchBusinessUnits/fetchBusinessUnitsApi";
import { BusinessUnit } from "@/api/fetchBusinessUnits/types";
import { createBusinessUnitTradeMappings } from "@/api/createBusinessUnitTradeMappings/createBusinessUnitTradeMappingsApi";
import { BusinessUnitTradeMappingRequest } from "@/api/createBusinessUnitTradeMappings/types";
import { getServiceTitanCredential } from "@/api/getServiceTitanCredential/getServiceTitanCredentialApi";
import { ServiceTitanCredential } from "@/api/getServiceTitanCredential/types";
import { saveServiceTitanCredential } from "@/api/saveServiceTitanCredential/saveServiceTitanCredentialApi";
import { triggerBusinessUnitSync } from "@/api/triggerBusinessUnitSync/triggerBusinessUnitSyncApi";
import { updateBusinessUnitWorkTypes } from "@/api/updateBusinessUnitWorkTypes/updateBusinessUnitWorkTypesApi";
import { ServiceTitanWorkType } from "@/api/updateBusinessUnitWorkTypes/types";
import { fetchBusinessUnitTradeMappings } from "@/api/fetchBusinessUnitTradeMappings/fetchBusinessUnitTradeMappingsApi";
import { BusinessUnitTradeMapping } from "@/api/fetchBusinessUnitTradeMappings/types";

export interface BusinessUnitMapping {
  id: string;
  businessUnitId: string;
  /**
   * ServiceTitan's own id, carried through so the table can recognise the
   * catch-all unit by the identity the API's rule is written in rather than by
   * the name the sync happens to give it. `businessUnitId` is hub's row id.
   */
  serviceTitanId: number;
  businessUnitName: string;
  tradeId: string;
  isLocked: boolean;
  /**
   * Deliberately outside `isLocked`. That lock exists because changing a trade
   * after invoices have synced would rewrite history; a work type is read
   * live when the dashboard renders, so there is no history to protect — and
   * gating it would leave every member who has already saved their trades
   * permanently unable to set one.
   */
  workType: ServiceTitanWorkType | null;
}

const DO_NOT_IMPORT = "do_not_import";

/**
 * A row's outstanding work-type writes.
 *
 * `queue` is the tail of the row's chain: the next PATCH waits on it rather
 * than racing it, so the server applies the row's changes in the order the
 * member made them. `generation` counts the changes issued for the row, and
 * only the latest is allowed to roll the row back — an earlier one's captured
 * value predates the choice now on screen.
 *
 * Per row, not per hook: a change to one business unit has no reason to wait
 * on another's.
 */
interface WorkTypeWrites {
  generation: number;
  queue: Promise<void>;
}

const loadBusinessUnitsAndMappings = async (companyUuid: string) => {
  const [businessUnitsResponse, mappingsResponse] = await Promise.all([
    fetchBusinessUnits(companyUuid),
    fetchBusinessUnitTradeMappings(companyUuid).catch(() => ({
      data: { mappings: [], total: 0 },
    })),
  ]);

  const existingMappings = new Map<number, BusinessUnitTradeMapping>();
  mappingsResponse.data.mappings.forEach((mapping) => {
    existingMappings.set(mapping.businessUnit.id, mapping);
  });

  return {
    businessUnits: businessUnitsResponse.data.businessUnits,
    existingMappings,
  };
};

const resolveSavedTradeId =
  (existingMappings: Map<number, BusinessUnitTradeMapping>) =>
  (businessUnit: BusinessUnit): string => {
    if (!businessUnit.isImportEnabled) {
      return DO_NOT_IMPORT;
    }
    const existing = existingMappings.get(businessUnit.id);
    return existing ? existing.trade.id.toString() : "";
  };

const buildMappings = (
  businessUnits: BusinessUnit[],
  resolveTradeId: (businessUnit: BusinessUnit) => string,
): BusinessUnitMapping[] =>
  businessUnits.map((businessUnit) => ({
    id: `mapping-${businessUnit.id}`,
    businessUnitId: businessUnit.id.toString(),
    serviceTitanId: businessUnit.serviceTitanId,
    businessUnitName: businessUnit.name,
    tradeId: resolveTradeId(businessUnit),
    isLocked: businessUnit.isLocked,
    workType: businessUnit.workType,
  }));

export const useServiceTitanIntegration = (companyUuid?: string) => {
  const { showNotification } = useNotification();

  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [businessUnitsLoading, setBusinessUnitsLoading] = useState(false);
  const [businessUnitsError, setBusinessUnitsError] = useState<string | null>(
    null,
  );

  const [trades, setTrades] = useState<Trade[]>([]);
  const [tradesLoading, setTradesLoading] = useState(true);
  const [tradesError, setTradesError] = useState<string | null>(null);

  const [mappings, setMappings] = useState<BusinessUnitMapping[]>([]);
  const [initialMappings, setInitialMappings] = useState<BusinessUnitMapping[]>(
    [],
  );

  /** Keyed by mapping id. See `WorkTypeWrites`. */
  const workTypeWrites = useRef(new Map<string, WorkTypeWrites>());

  /**
   * The rows as they are on screen right now, for the one caller that has to
   * read them *after* awaiting a request. `handleConfirmSave` needs the same
   * array twice — `setMappings` and `setInitialMappings` — so it cannot use a
   * functional updater, and the render closure it would otherwise read
   * predates its own request.
   */
  const mappingsRef = useRef<BusinessUnitMapping[]>([]);

  useEffect(() => {
    mappingsRef.current = mappings;
  }, [mappings]);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSavingMappings, setIsSavingMappings] = useState(false);

  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [credential, setCredential] = useState<ServiceTitanCredential | null>(
    null,
  );
  const [credentialLoading, setCredentialLoading] = useState(true);
  const [credentialError, setCredentialError] = useState<string | null>(null);

  const [isEditingCredentials, setIsEditingCredentials] = useState(false);
  const [editedTenantId, setEditedTenantId] = useState("");
  const [editedClientId, setEditedClientId] = useState("");
  const [editedClientSecret, setEditedClientSecret] = useState("");
  const [isSavingCredentials, setIsSavingCredentials] = useState(false);
  const [credentialsSaveError, setCredentialsSaveError] = useState<
    string | null
  >(null);
  const [showClientSecret, setShowClientSecret] = useState(false);

  useEffect(() => {
    const loadTrades = async () => {
      try {
        setTradesLoading(true);
        setTradesError(null);
        const response = await fetchTrades();
        setTrades(response.data);
      } catch (error) {
        console.error("Failed to fetch trades:", error);
        setTradesError("Failed to load trade options");
      } finally {
        setTradesLoading(false);
      }
    };

    loadTrades();
  }, [companyUuid]);

  useEffect(() => {
    if (!companyUuid || companyUuid === "0") {
      setCredentialLoading(false);
      return;
    }

    const loadCredential = async () => {
      try {
        setCredentialLoading(true);
        setCredentialError(null);
        const response = await getServiceTitanCredential(companyUuid);
        const cred = response.data.credential;
        setCredential(cred);

        if (cred) {
          setEditedTenantId(cred.tenantId);
          setEditedClientId(cred.clientId);
          setEditedClientSecret(""); // Don't prefill secret for security
          setLastSyncTime(
            cred.updatedAt ? new Date(cred.updatedAt).toLocaleString() : null,
          );
        }
      } catch (error) {
        console.error("Failed to load credential:", error);
        setCredentialError("Failed to load ServiceTitan credentials");
      } finally {
        setCredentialLoading(false);
      }
    };

    loadCredential();
  }, [companyUuid]);

  useEffect(() => {
    if (!credential?.tenantId) {
      setBusinessUnits([]);
      setMappings([]);
      return;
    }

    const loadBusinessUnits = async () => {
      if (!companyUuid) {
        setBusinessUnitsError(
          "Company UUID is required to load business units",
        );
        return;
      }

      try {
        setBusinessUnitsLoading(true);
        setBusinessUnitsError(null);

        const { businessUnits: units, existingMappings } =
          await loadBusinessUnitsAndMappings(companyUuid);

        setBusinessUnits(units);

        const newMappings = buildMappings(
          units,
          resolveSavedTradeId(existingMappings),
        );

        setMappings(newMappings);
        setInitialMappings(newMappings);
      } catch (error) {
        console.error("Failed to fetch business units:", error);
        setBusinessUnitsError("Failed to load business units");
      } finally {
        setBusinessUnitsLoading(false);
      }
    };

    loadBusinessUnits();
  }, [credential]);

  const handleMappingChange = (
    mappingId: string,
    field: "businessUnitId" | "tradeId",
    value: string,
  ) => {
    setMappings((prev) =>
      prev.map((mapping) =>
        mapping.id === mappingId ? { ...mapping, [field]: value } : mapping,
      ),
    );
  };

  /**
   * Saved on its own, the moment it changes.
   *
   * Not folded into "Save Mappings": that path locks the rows it touches and
   * enforces "at least one business unit must be mapped to a trade", neither of
   * which has anything to do with a work type. Bundling them would make a
   * work type inherit a lock it has no reason to obey.
   *
   * **A row's writes are sequenced.** Fired together, two changes to one row
   * are two requests with no ordering between them, and the slower one's write
   * lands last - so the server keeps a value the member deselected, with no
   * failure, no toast and nothing on screen to disagree with. The dashboard
   * then reads that work type live and computes their KPIs from it. Waiting on
   * the row's previous write costs nothing the member sees (the dropdown is
   * already optimistic) and makes the server's final value their last choice.
   *
   * The residual: two consecutive failures on one row roll it back to the
   * first failure's value rather than to the last value the server accepted.
   * Both have already raised a toast, so the member is not being told nothing.
   */
  const handleWorkTypeChange = async (
    mappingId: string,
    businessUnitId: string,
    workType: ServiceTitanWorkType | null,
  ) => {
    if (!companyUuid) return;

    const previousWorkType =
      mappings.find((mapping) => mapping.id === mappingId)?.workType ?? null;

    setMappings((current) =>
      current.map((mapping) =>
        mapping.id === mappingId ? { ...mapping, workType } : mapping,
      ),
    );

    const previous = workTypeWrites.current.get(mappingId);
    const generation = (previous?.generation ?? 0) + 1;

    const queue = (previous?.queue ?? Promise.resolve()).then(async () => {
      try {
        await updateBusinessUnitWorkTypes(companyUuid, {
          workTypes: [{ businessUnitId: parseInt(businessUnitId), workType }],
        });
      } catch (error) {
        console.error("Failed to save work type:", error);

        // A superseded change must not write its captured value back: the
        // member has already chosen something else, and the request carrying
        // that choice is queued behind this one or has already succeeded.
        if (workTypeWrites.current.get(mappingId)?.generation !== generation) {
          return;
        }

        // The optimistic update has to come back out, or the dropdown keeps
        // showing a value the server rejected. The interceptor has already
        // told the member what went wrong.
        //
        // One field on one row, functionally, for the same reason the update
        // above is functional: writing back a whole array captured before the
        // request would also undo anything else that changed while it was in
        // flight - an unsaved trade edit on another row.
        setMappings((current) =>
          current.map((mapping) =>
            mapping.id === mappingId
              ? { ...mapping, workType: previousWorkType }
              : mapping,
          ),
        );
      }
    });

    workTypeWrites.current.set(mappingId, { generation, queue });

    return queue;
  };

  const changedMappings = useMemo(() => {
    const initialMap = new Map(initialMappings.map((m) => [m.id, m.tradeId]));
    return mappings.filter(
      (m) => !m.isLocked && m.tradeId !== initialMap.get(m.id),
    );
  }, [mappings, initialMappings]);

  const hasUnassigned = mappings.some((m) => !m.isLocked && m.tradeId === "");
  const allDoNotImport =
    mappings.length > 0 && mappings.every((m) => m.tradeId === DO_NOT_IMPORT);
  const hasChanges =
    changedMappings.length > 0 && !hasUnassigned && !allDoNotImport;

  const changedMappingSummary = useMemo(() => {
    return changedMappings.map((m) => {
      const trade = trades.find((t) => t.id.toString() === m.tradeId);
      return {
        businessUnitName: m.businessUnitName,
        tradeName:
          m.tradeId === DO_NOT_IMPORT
            ? "Do not import"
            : m.tradeId === ""
              ? "Unassigned"
              : trade?.name || m.tradeId,
      };
    });
  }, [changedMappings, trades]);

  // From the mapping as last saved, not the dropdowns: the nightly sync
  // blocks the import on what the server holds.
  const isImportBlocked = initialMappings.some((m) => m.tradeId === "");

  const lastSyncedLabel = credential?.lastSuccessfulSyncAt
    ? new Date(credential.lastSuccessfulSyncAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : null;

  const incompleteMappings = mappings.filter(
    (m) => !m.businessUnitId || !m.tradeId || m.tradeId === "",
  ).length;

  const handleConfirmSave = async () => {
    if (!companyUuid) return;

    try {
      setIsSavingMappings(true);

      const apiMappings: BusinessUnitTradeMappingRequest[] =
        changedMappings.map((m) => ({
          businessUnitId: parseInt(m.businessUnitId),
          tradeId: m.tradeId === DO_NOT_IMPORT ? 0 : parseInt(m.tradeId),
        }));

      await createBusinessUnitTradeMappings(companyUuid, {
        mappings: apiMappings,
      });

      // Through the ref, not the closure: a work-type write issued before the
      // modal opened can come back while this request is in flight, and the
      // array captured at the top of it no longer describes the table.
      const changedIds = new Set(changedMappings.map((m) => m.id));
      const updatedMappings = mappingsRef.current.map((m) =>
        changedIds.has(m.id) ? { ...m, isLocked: true } : m,
      );
      setMappings(updatedMappings);
      setInitialMappings(updatedMappings);
      setIsConfirmModalOpen(false);
      setLastSyncTime("just now");

      showNotification(
        "Mappings Saved",
        "Trade mappings have been saved successfully.",
        "success",
      );
    } catch (error) {
      console.error("Failed to save mappings:", error);
      const message =
        axios.isAxiosError(error) && error.response?.data?.errors?.detail
          ? error.response.data.errors.detail
          : "Failed to save trade mappings. Please try again.";
      showNotification("Save Failed", message, "error");
    } finally {
      setIsSavingMappings(false);
    }
  };

  const handleRefreshBusinessUnits = async () => {
    if (!credential?.tenantId || !companyUuid) return;

    setIsRefreshing(true);
    try {
      setBusinessUnitsError(null);

      await triggerBusinessUnitSync(companyUuid);

      const { businessUnits: units, existingMappings } =
        await loadBusinessUnitsAndMappings(companyUuid);

      setBusinessUnits(units);

      const currentTradeIds = new Map(
        mappings.map((m) => [m.businessUnitId, m.tradeId]),
      );

      // The dropdowns keep the member's unsaved choices; the saved copy stays
      // what the server holds, so they still count as changes.
      const savedMappings = buildMappings(
        units,
        resolveSavedTradeId(existingMappings),
      );
      const newMappings = savedMappings.map((m) => {
        const currentTradeId = currentTradeIds.get(m.businessUnitId);
        return currentTradeId ? { ...m, tradeId: currentTradeId } : m;
      });

      setMappings(newMappings);
      setInitialMappings(savedMappings);
      setLastSyncTime("just now");
    } catch (error) {
      console.error("Failed to refresh business units:", error);
      setBusinessUnitsError("Failed to refresh business units");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!companyUuid) return;

    setIsSavingCredentials(true);
    setCredentialsSaveError(null);

    try {
      const response = await saveServiceTitanCredential(companyUuid, {
        tenantId: editedTenantId,
        clientId: editedClientId,
        clientSecret: editedClientSecret,
      });

      if (response.success && response.credential) {
        const savedCredential = response.credential;
        setCredential((previous) => ({
          ...savedCredential,
          lastSuccessfulSyncAt: previous?.lastSuccessfulSyncAt ?? null,
        }));
        setIsEditingCredentials(false);
        setLastSyncTime(new Date().toLocaleString());
        setShowClientSecret(false);

        showNotification(
          "Credentials Saved",
          "ServiceTitan credentials have been saved and verified successfully!",
          "success",
        );

        await triggerBusinessUnitSync(companyUuid);

        const { businessUnits: units, existingMappings } =
          await loadBusinessUnitsAndMappings(companyUuid);

        setBusinessUnits(units);

        const newMappings = buildMappings(
          units,
          resolveSavedTradeId(existingMappings),
        );

        setMappings(newMappings);
        setInitialMappings(newMappings);
      }
    } catch (error) {
      console.error("Failed to save credentials:", error);
      const errorMessage =
        axios.isAxiosError(error) && error.response?.data?.errors?.detail
          ? error.response.data.errors.detail
          : "Failed to save credentials. Please check your credentials and try again.";
      setCredentialsSaveError(errorMessage);
    } finally {
      setIsSavingCredentials(false);
    }
  };

  const handleCancelEdit = () => {
    if (credential) {
      setEditedTenantId(credential.tenantId);
      setEditedClientId(credential.clientId);
      setEditedClientSecret("");
    } else {
      setEditedTenantId("");
      setEditedClientId("");
      setEditedClientSecret("");
    }
    setIsEditingCredentials(false);
    setCredentialsSaveError(null);
    setShowClientSecret(false);
  };

  return {
    allDoNotImport,
    businessUnits,
    businessUnitsError,
    businessUnitsLoading,
    changedMappingSummary,
    credential,
    credentialError,
    credentialLoading,
    credentialsSaveError,
    editedClientId,
    editedClientSecret,
    editedTenantId,
    handleCancelEdit,
    handleConfirmSave,
    handleWorkTypeChange,
    handleMappingChange,
    handleRefreshBusinessUnits,
    handleSaveCredentials,
    hasChanges,
    incompleteMappings,
    isConfirmModalOpen,
    isEditingCredentials,
    isImportBlocked,
    isRefreshing,
    isSavingCredentials,
    isSavingMappings,
    lastSyncedLabel,
    lastSyncTime,
    mappings,
    setEditedClientId,
    setEditedClientSecret,
    setEditedTenantId,
    setIsConfirmModalOpen,
    setIsEditingCredentials,
    setShowClientSecret,
    showClientSecret,
    trades,
    tradesError,
    tradesLoading,
  };
};

export type ServiceTitanIntegrationState = ReturnType<
  typeof useServiceTitanIntegration
>;
